// Recipe UI Localization Strings

import { SupportedLanguage } from './recipe-types';

// UI strings for recipe pages
export interface RecipeUIStrings {
  // Navigation
  backToEats: string;
  
  // Meta sections
  prepTime: string;
  cookTime: string;
  totalTime: string;
  servings: string;
  difficulty: string;
  ageRange: string;
  
  // Recipe sections
  ingredients: string;
  instructions: string;
  safetyNotes: string;
  beforeYouStart: string;
  nutritionInfo: string;
  allergenInfo: string;
  
  // Nutrition labels
  calories: string;
  protein: string;
  carbs: string;
  fat: string;
  perServing: string;
  
  // Difficulty levels
  easy: string;
  medium: string;
  hard: string;
  
  // Audio
  listenToRecipe: string;
  playAudio: string;
  pauseAudio: string;
  audioComingSoon: string;
  
  // Actions
  saveRecipe: string;
  unsaveRecipe: string;
  share: string;
  print: string;
  
  // Time format
  minutes: string;
  hours: string;
  
  // General
  by: string;
  readTime: string;
  bestForAges: string;
}

export const RECIPE_UI_STRINGS: Record<SupportedLanguage, RecipeUIStrings> = {
  en: {
    backToEats: 'Back to Eats',
    prepTime: 'Prep Time',
    cookTime: 'Cook Time',
    totalTime: 'Total Time',
    servings: 'Servings',
    difficulty: 'Difficulty',
    ageRange: 'Age Range',
    ingredients: 'Ingredients',
    instructions: 'Instructions',
    safetyNotes: 'Safety Notes',
    beforeYouStart: 'Before You Start',
    nutritionInfo: 'Nutrition',
    allergenInfo: 'Allergen Information',
    calories: 'Calories',
    protein: 'Protein',
    carbs: 'Carbs',
    fat: 'Fat',
    perServing: 'per serving',
    easy: 'Easy',
    medium: 'Medium',
    hard: 'Hard',
    listenToRecipe: 'Listen to this recipe',
    playAudio: 'Play audio recipe',
    pauseAudio: 'Pause audio',
    audioComingSoon: 'Audio coming soon',
    saveRecipe: 'Save Recipe',
    unsaveRecipe: 'Unsave Recipe',
    share: 'Share',
    print: 'Print',
    minutes: 'minutes',
    hours: 'hours',
    by: 'By',
    readTime: 'min read',
    bestForAges: 'Best for ages',
  },
  es: {
    backToEats: 'Volver a Recetas',
    prepTime: 'Tiempo de Preparación',
    cookTime: 'Tiempo de Cocción',
    totalTime: 'Tiempo Total',
    servings: 'Porciones',
    difficulty: 'Dificultad',
    ageRange: 'Rango de Edad',
    ingredients: 'Ingredientes',
    instructions: 'Instrucciones',
    safetyNotes: 'Notas de Seguridad',
    beforeYouStart: 'Antes de Comenzar',
    nutritionInfo: 'Nutrición',
    allergenInfo: 'Información de Alérgenos',
    calories: 'Calorías',
    protein: 'Proteína',
    carbs: 'Carbohidratos',
    fat: 'Grasa',
    perServing: 'por porción',
    easy: 'Fácil',
    medium: 'Medio',
    hard: 'Difícil',
    listenToRecipe: 'Escucha esta receta',
    playAudio: 'Reproducir audio de receta',
    pauseAudio: 'Pausar audio',
    audioComingSoon: 'Audio próximamente',
    saveRecipe: 'Guardar Receta',
    unsaveRecipe: 'Eliminar Receta',
    share: 'Compartir',
    print: 'Imprimir',
    minutes: 'minutos',
    hours: 'horas',
    by: 'Por',
    readTime: 'min de lectura',
    bestForAges: 'Ideal para edades',
  },
  fr: {
    backToEats: 'Retour aux Recettes',
    prepTime: 'Temps de Préparation',
    cookTime: 'Temps de Cuisson',
    totalTime: 'Temps Total',
    servings: 'Portions',
    difficulty: 'Difficulté',
    ageRange: 'Tranche d\'âge',
    ingredients: 'Ingrédients',
    instructions: 'Instructions',
    safetyNotes: 'Notes de Sécurité',
    beforeYouStart: 'Avant de Commencer',
    nutritionInfo: 'Nutrition',
    allergenInfo: 'Informations sur les Allergènes',
    calories: 'Calories',
    protein: 'Protéines',
    carbs: 'Glucides',
    fat: 'Graisses',
    perServing: 'par portion',
    easy: 'Facile',
    medium: 'Moyen',
    hard: 'Difficile',
    listenToRecipe: 'Écoutez cette recette',
    playAudio: 'Lire l\'audio de la recette',
    pauseAudio: 'Mettre en pause',
    audioComingSoon: 'Audio bientôt disponible',
    saveRecipe: 'Enregistrer la Recette',
    unsaveRecipe: 'Retirer la Recette',
    share: 'Partager',
    print: 'Imprimer',
    minutes: 'minutes',
    hours: 'heures',
    by: 'Par',
    readTime: 'min de lecture',
    bestForAges: 'Idéal pour les âges',
  },
  de: {
    backToEats: 'Zurück zu Rezepten',
    prepTime: 'Vorbereitungszeit',
    cookTime: 'Kochzeit',
    totalTime: 'Gesamtzeit',
    servings: 'Portionen',
    difficulty: 'Schwierigkeit',
    ageRange: 'Altersbereich',
    ingredients: 'Zutaten',
    instructions: 'Anleitung',
    safetyNotes: 'Sicherheitshinweise',
    beforeYouStart: 'Bevor Sie Beginnen',
    nutritionInfo: 'Nährwertangaben',
    allergenInfo: 'Allergeninformationen',
    calories: 'Kalorien',
    protein: 'Protein',
    carbs: 'Kohlenhydrate',
    fat: 'Fett',
    perServing: 'pro Portion',
    easy: 'Einfach',
    medium: 'Mittel',
    hard: 'Schwer',
    listenToRecipe: 'Höre dieses Rezept',
    playAudio: 'Rezept-Audio abspielen',
    pauseAudio: 'Audio pausieren',
    audioComingSoon: 'Audio demnächst verfügbar',
    saveRecipe: 'Rezept Speichern',
    unsaveRecipe: 'Rezept Entfernen',
    share: 'Teilen',
    print: 'Drucken',
    minutes: 'Minuten',
    hours: 'Stunden',
    by: 'Von',
    readTime: 'Min. Lesezeit',
    bestForAges: 'Ideal für Alter',
  },
};

/**
 * Get UI strings for a specific language
 */
export function getRecipeUIStrings(lang: SupportedLanguage): RecipeUIStrings {
  return RECIPE_UI_STRINGS[lang];
}

/**
 * Format difficulty level in the specified language
 */
export function formatDifficulty(
  difficulty: 'Easy' | 'Medium' | 'Hard',
  lang: SupportedLanguage
): string {
  const strings = RECIPE_UI_STRINGS[lang];
  const difficultyMap: Record<string, string> = {
    Easy: strings.easy,
    Medium: strings.medium,
    Hard: strings.hard,
  };
  return difficultyMap[difficulty] || difficulty;
}

